import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import type { OrgWithId } from "@/hooks/useOrg";
import { useCivicStudios } from "@/hooks/useCivicStudios";
import { db } from "@/lib/firebase";
import { converters } from "@/lib/firestore";

export type OrgAppKind = "lca" | "civic" | "support";

export interface OrgAppEntry {
  key: string;
  kind: OrgAppKind;
  name: string;
  to: string;
}

interface OrgAppsOrgState {
  scope: string | null;
  org: OrgWithId | null;
}

function emptyOrgState(scope: string | null): OrgAppsOrgState {
  return { scope, org: null };
}

/**
 * Resolves the apps available to an org, gated by its feature flags:
 * - LCA Processing when `lca_assessment` is enabled (org-scoped)
 * - City Administration per configured civic studio when `civic_assistant` is
 *   enabled (studio-scoped)
 *
 * Returns the loaded org doc too, so callers that also need the org (e.g. the
 * sidebar header) don't open a second listener. Pass `enabled = false` to stay
 * idle (e.g. for platform users, who use a static nav).
 */
export function useOrgApps(
  orgId: string | undefined,
  enabled: boolean
): { org: OrgWithId | null; apps: OrgAppEntry[] } {
  const requestedScope = enabled && orgId ? orgId : null;
  const [orgState, setOrgState] = useState<OrgAppsOrgState>(() =>
    emptyOrgState(requestedScope)
  );

  useEffect(() => {
    let cancelled = false;
    setOrgState(emptyOrgState(requestedScope));

    if (!requestedScope) return;

    const ref = doc(db, "orgs", requestedScope).withConverter(converters.orgs);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        setOrgState({
          scope: requestedScope,
          org: snap.exists()
            ? ({ id: snap.id, ...snap.data() } as OrgWithId)
            : null,
        });
      },
      (err) => {
        if (cancelled) return;
        console.error("Error fetching org:", err);
        setOrgState(emptyOrgState(requestedScope));
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [requestedScope]);

  // Scope-tagged state prevents the prior organization from appearing during
  // the render that precedes effect teardown and listener replacement.
  const org = orgState.scope === requestedScope ? orgState.org : null;

  const lcaEnabled = org?.features?.lca_assessment?.enabled === true;
  const civicEnabled = org?.features?.civic_assistant?.enabled === true;
  const civicStudios = useCivicStudios(orgId, enabled && civicEnabled);

  const apps = useMemo<OrgAppEntry[]>(() => {
    if (!org) return [];
    const list: OrgAppEntry[] = [];
    if (org.features?.support?.enabled === true) list.push({ key: "support", kind: "support", name: "BiG Support", to: "/org/support" });
    if (lcaEnabled) {
      list.push({
        key: "lca",
        kind: "lca",
        name: "LCA Processing",
        to: "/org/apps/lca-assessment",
      });
    }
    if (civicEnabled) {
      for (const studio of civicStudios) {
        list.push({
          key: `civic-${studio.id}`,
          kind: "civic",
          name:
            civicStudios.length > 1
              ? `City Administration · ${studio.name}`
              : "City Administration",
          to: `/org/studios/${studio.id}/civic`,
        });
      }
    }
    return list;
  }, [org, lcaEnabled, civicEnabled, civicStudios]);

  return { org, apps };
}
